import React from 'react';
import { observer } from 'mobx-react-lite';
import { ColumnsIcon } from '@radix-ui/react-icons';
import { SegmentedControl } from '@producer/ui-kit';

import { SaveTaskDto, Task, TaskLevel } from '../../../app/entities';
import { ToolBar } from '../../../components/toolbar/toolbar';
import { ListDisplayIcon } from '../../../components/icons';
import { taskGroupingStore } from '../../../core/stores/task-store';
import { GroupingDropdown } from './grouping-dropdown';
import { SpaceTasksFilters } from './space-tasks-filters/space-tasks-filters';
import { ProjectTasksFilters } from './project-tasks-filters/project-tasks-filters';
import { TaskList } from './tasks-list';
import { TaskBoard } from './board/task-board';

import styles from './tasks-view.module.scss';
type TasksViewProps = {
  tasks: Task[];
  taskLevel: TaskLevel;
  onClearFilters?: () => void;
  onSelectTask?: (taskId: string) => void;
  defaultValues?: Partial<SaveTaskDto>;
};

/**
 * Wraps the tasks toolbar (filters + grouping + List/Kanban toggle) and renders
 * either the list or the kanban board based on `taskGroupingStore.viewType`.
 * Used by the project and space tasks routes.
 */
export const TasksView: React.FC<TasksViewProps> = observer(
  ({ tasks, taskLevel, onClearFilters, onSelectTask, defaultValues }) => {
    const viewType = taskGroupingStore.viewType;

    return (
      <>
        <ToolBar>
          {taskLevel === 'space' ? <SpaceTasksFilters /> : <ProjectTasksFilters />}

          <div />

          <div className={styles['right']}>
            <div className={styles['view-switch']}>
              <SegmentedControl
                key="change-layout-button"
                onClick={() =>
                  taskGroupingStore.setViewType(viewType === 'list' ? 'kanban' : 'list')
                }
              >
                <span data-active={viewType === 'kanban'}>
                  <ColumnsIcon />
                </span>

                <span data-active={viewType === 'list'}>
                  <ListDisplayIcon />
                </span>
              </SegmentedControl>
            </div>

            <GroupingDropdown taskLevel={taskLevel} />
          </div>
        </ToolBar>

        {viewType === 'kanban' ? (
          <TaskBoard
            key={taskGroupingStore.groupingKey}
            tasks={tasks}
            taskLevel={taskLevel}
            onSelectTask={onSelectTask}
          />
        ) : (
          <TaskList
            key={taskGroupingStore.groupingKey}
            tasks={tasks}
            taskLevel={taskLevel}
            onClearFilters={onClearFilters}
            onSelectTask={onSelectTask}
            defaultValues={defaultValues}
            hideToolbar
          />
        )}
      </>
    );
  },
);

/**
 * The radix ColumnsIcon paints with currentColor, so when unselected it would
 * inherit the toolbar's bright text color. Force the muted low-contrast tone on
 * the inactive icon to match the custom ListDisplayIcon; the SegmentedControl
 * itself turns the active icon white.
 */
